import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Page,
  Card,
  Text,
  Banner,
  InlineStack,
  InlineGrid,
  BlockStack,
  Box,
  Badge,
  DropZone,
  Spinner,
  DataTable,
  Tabs,
  Thumbnail,
  Link,
  TextField,
  Select,
  Checkbox,
  Button,
  Modal,
  Scrollable
} from '@shopify/polaris';
import { TabTitle } from '../../shell/helmet.jsx';
import ProjectSelect from '../../shared/components/projectSelect.jsx';
import CompanySelect from '../../shared/components/companySelect.jsx';
import BomPurchaseOrdersBanner from './bomPurchaseOrdersBanner.jsx';

const STALE_PRICE_MONTHS = 12;

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '—';
  return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(value);
};

const formatNumber = (value) => {
  if (value == null || isNaN(value)) return '0';
  return new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 2 }).format(value);
};

// Supplier group of a line in the purchase order modal: the DB supplier, or for parts without one the vendor
// from the file (those lines go to the unknown supplier unless another supplier is picked for the group)
const supplierGroupKey = (line) => (line.supplierId != null ? `id:${line.supplierId}` : `vendor:${(line.supplierName || '').trim().toLowerCase()}`);

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
};

function SummaryCard({ title, value, subtitle, tone }) {
  return (
    <Card>
      <Box padding="400" minWidth="180px">
        <Text variant="headingXs" tone="subdued">
          {title}
        </Text>
        <Box paddingBlockStart="100">
          <Text variant="headingLg" fontWeight="bold" tone={tone}>
            {value}
          </Text>
        </Box>
        {subtitle && (
          <Box paddingBlockStart="100">
            <Text variant="bodySm" tone="subdued">
              {subtitle}
            </Text>
          </Box>
        )}
      </Box>
    </Card>
  );
}

const sortRows = (rows, accessors, index, direction) => {
  const accessor = accessors[index];
  if (!accessor) return rows;
  const factor = direction === 'ascending' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const valueA = accessor(a);
    const valueB = accessor(b);
    if (valueA == null && valueB == null) return 0;
    if (valueA == null) return 1; // nulls always sink to the bottom
    if (valueB == null) return -1;
    if (typeof valueA === 'number' && typeof valueB === 'number') return (valueA - valueB) * factor;
    return String(valueA).localeCompare(String(valueB), undefined, { numeric: true, sensitivity: 'base' }) * factor;
  });
};

const RESULT_TABS = [
  { id: 'materials', content: 'Materials' },
  { id: 'suppliers', content: 'Cost per supplier' }
];

const STATUS_FILTER_OPTIONS = [
  { label: 'All lines', value: 'all' },
  { label: 'Matched in DB', value: 'matched' },
  { label: 'Unmatched', value: 'unmatched' },
  { label: `Aged price (> ${STALE_PRICE_MONTHS} months)`, value: 'aged' },
  { label: 'No price in DB', value: 'noprice' },
  { label: 'Pickable in atelier', value: 'pickable' },
  { label: 'Stock shortage in atelier', value: 'shortage' },
  { label: 'Discontinued', value: 'discontinued' }
];

export default function BomCostToolPage() {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [materialSort, setMaterialSort] = useState({ index: 10, direction: 'descending' });
  const [supplierSort, setSupplierSort] = useState({ index: 3, direction: 'descending' });

  // filters
  const [searchQuery, setSearchQuery] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // selection + purchase order creation
  const [selectedRows, setSelectedRows] = useState(() => new Set());
  const [poModalOpen, setPoModalOpen] = useState(false);
  const [poProjectId, setPoProjectId] = useState(null);
  const [poCreating, setPoCreating] = useState(false);
  const [poError, setPoError] = useState('');
  const [poResult, setPoResult] = useState(null);
  const [supplierOverrides, setSupplierOverrides] = useState(() => new Map()); // supplier group key -> { id, name }
  const [excludedDocumentIds, setExcludedDocumentIds] = useState(() => new Set()); // part documents not to copy
  // part documents read again when the modal opens: { loading, byProduct: Map<productId, documents> | null }
  const [partDocuments, setPartDocuments] = useState({ loading: false, byProduct: null });
  const partDocumentsRequest = useRef(0);

  // enlarged thumbnail preview: { src, title } or null
  const [thumbPreview, setThumbPreview] = useState(null);

  const resetAll = () => {
    setAnalysis(null);
    setSelectedRows(new Set());
    setPoResult(null);
    setPoError('');
    setPoProjectId(null);
    setSupplierOverrides(new Map());
    setExcludedDocumentIds(new Set());
    setSearchQuery('');
    setSupplierFilter('all');
    setStatusFilter('all');
  };

  const handleDrop = useCallback(async (_dropFiles, acceptedFiles, rejectedFiles) => {
    setError('');
    if (rejectedFiles?.length) {
      setError('Only .xlsx files exported from Inventor are supported.');
      return;
    }
    const file = acceptedFiles?.[0];
    if (!file) return;

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('bom', file);
      const response = await fetch('/api/reports/bom-cost-analysis', { method: 'POST', body: formData });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || `Analysis failed (${response.status})`);
      setAnalysis(data);
      setSelectedTab(0);
      setSelectedRows(new Set());
      setPoResult(null);
      setSupplierOverrides(new Map());
      setExcludedDocumentIds(new Set());
      setMaterialSort({ index: 10, direction: 'descending' });
      setSupplierSort({ index: 3, direction: 'descending' });
    } catch (e) {
      setError(e.message || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  }, []);

  const staleCutoff = useMemo(() => {
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - STALE_PRICE_MONTHS);
    return cutoff;
  }, []);

  const enrichedLines = useMemo(() => {
    if (!analysis?.lines) return [];
    return analysis.lines.map((line) => {
      const matched = line.status === 'matched';
      const priceDate = line.priceUpdatedAt ? new Date(line.priceUpdatedAt) : null;
      const priceDateValid = priceDate && !isNaN(priceDate.getTime());
      return {
        ...line,
        priceDateMs: priceDateValid ? priceDate.getTime() : null,
        // A matched part whose purchase price was never updated is treated as aged too
        priceStale: matched && (!priceDateValid || priceDate < staleCutoff),
        priceMissing: matched && (line.unitPrice == null || line.unitPrice === 0),
        pickable: matched && (line.stockAtelierQty ?? 0) >= line.qty,
        stockShort: matched && (line.stockAtelierQty ?? 0) < line.qty
      };
    });
  }, [analysis, staleCutoff]);

  const summary = useMemo(() => {
    const totalLines = enrichedLines.length;
    const matchedLines = enrichedLines.filter((line) => line.status === 'matched');
    const notInDb = enrichedLines.filter((line) => line.status === 'not_in_db').length;
    const noProductId = enrichedLines.filter((line) => line.status === 'no_product_id').length;
    const totalCost = matchedLines.reduce((sum, line) => sum + (line.lineTotal || 0), 0);
    const staleLines = matchedLines.filter((line) => line.priceStale);
    const staleCost = staleLines.reduce((sum, line) => sum + (line.lineTotal || 0), 0);
    const missingPrice = matchedLines.filter((line) => line.priceMissing).length;
    const totalQty = enrichedLines.reduce((sum, line) => sum + (line.qty || 0), 0);
    const coverage = totalLines ? (matchedLines.length / totalLines) * 100 : 0;
    const supplierCount = new Set(matchedLines.map((line) => line.supplierName || 'Unknown')).size;
    const uniqueParts = new Set(enrichedLines.map((line) => line.productId ?? `file:${line.partNumber || line.rowNumber}`)).size;
    const pickableCount = matchedLines.filter((line) => line.pickable).length;
    const shortageCount = matchedLines.filter((line) => line.stockShort).length;
    const discontinuedCount = matchedLines.filter((line) => line.discontinued).length;
    return {
      totalLines,
      matchedCount: matchedLines.length,
      notInDb,
      noProductId,
      totalCost,
      staleCount: staleLines.length,
      staleCost,
      missingPrice,
      totalQty,
      coverage,
      supplierCount,
      uniqueParts,
      pickableCount,
      shortageCount,
      discontinuedCount
    };
  }, [enrichedLines]);

  const supplierAggregates = useMemo(() => {
    const groups = new Map();
    enrichedLines.forEach((line) => {
      const key = line.supplierName || 'Unknown';
      if (!groups.has(key)) {
        groups.set(key, { supplierName: key, supplierId: null, cost: 0, qty: 0, uniqueParts: new Set(), staleCount: 0, unmatchedCount: 0 });
      }
      const group = groups.get(key);
      if (group.supplierId == null && line.supplierId != null) group.supplierId = line.supplierId;
      group.qty += line.qty || 0;
      group.uniqueParts.add(line.productId ?? `file:${line.partNumber || line.rowNumber}`);
      if (line.status === 'matched') {
        group.cost += line.lineTotal || 0;
        if (line.priceStale) group.staleCount += 1;
      } else {
        group.unmatchedCount += 1;
      }
    });
    return Array.from(groups.values()).map((group) => ({ ...group, uniqueParts: group.uniqueParts.size }));
  }, [enrichedLines]);

  const supplierFilterOptions = useMemo(() => {
    const names = Array.from(new Set(enrichedLines.map((line) => line.supplierName || 'Unknown'))).sort((a, b) => a.localeCompare(b));
    return [{ label: 'All suppliers', value: 'all' }, ...names.map((name) => ({ label: name, value: name }))];
  }, [enrichedLines]);

  const filteredLines = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return enrichedLines.filter((line) => {
      if (supplierFilter !== 'all' && (line.supplierName || 'Unknown') !== supplierFilter) return false;
      if (statusFilter === 'matched' && line.status !== 'matched') return false;
      if (statusFilter === 'unmatched' && line.status === 'matched') return false;
      if (statusFilter === 'aged' && !line.priceStale) return false;
      if (statusFilter === 'noprice' && !line.priceMissing) return false;
      if (statusFilter === 'pickable' && !line.pickable) return false;
      if (statusFilter === 'shortage' && !line.stockShort) return false;
      if (statusFilter === 'discontinued' && !line.discontinued) return false;
      if (query) {
        const haystack = [line.productName, line.partNumber, line.productRef, line.productId, line.supplierName].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [enrichedLines, searchQuery, supplierFilter, statusFilter]);

  const hasActiveFilters = searchQuery.trim() !== '' || supplierFilter !== 'all' || statusFilter !== 'all';

  const toggleRow = (rowNumber) => {
    setSelectedRows((previous) => {
      const next = new Set(previous);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return next;
    });
  };

  // select-all leaves discontinued parts out so they are not ordered by accident; they can still be ticked one by one
  // (or all at once with the "Discontinued" status filter)
  const bulkSelectableLines = useMemo(() => (statusFilter === 'discontinued' ? filteredLines : filteredLines.filter((line) => !line.discontinued)), [filteredLines, statusFilter]);
  const allFilteredSelected = bulkSelectableLines.length > 0 && bulkSelectableLines.every((line) => selectedRows.has(line.rowNumber));
  const someFilteredSelected = filteredLines.some((line) => selectedRows.has(line.rowNumber));

  const toggleAllFiltered = () => {
    setSelectedRows((previous) => {
      const next = new Set(previous);
      if (allFilteredSelected) filteredLines.forEach((line) => next.delete(line.rowNumber));
      else bulkSelectableLines.forEach((line) => next.add(line.rowNumber));
      return next;
    });
  };

  const selectedLines = useMemo(() => enrichedLines.filter((line) => selectedRows.has(line.rowNumber)), [enrichedLines, selectedRows]);
  const selectedDiscontinuedLines = useMemo(() => selectedLines.filter((line) => line.discontinued), [selectedLines]);
  const selectedTotal = useMemo(() => selectedLines.reduce((sum, line) => sum + (line.lineTotal ?? (line.filePrice != null ? line.filePrice * line.qty : 0)), 0), [selectedLines]);

  const selectedBySupplier = useMemo(() => {
    const groups = new Map();
    selectedLines.forEach((line) => {
      const key = supplierGroupKey(line);
      if (!groups.has(key)) groups.set(key, { key, supplierId: line.supplierId ?? null, supplierName: line.supplierName || 'Unknown supplier', lineCount: 0, total: 0 });
      const group = groups.get(key);
      group.lineCount += 1;
      group.total += line.lineTotal ?? (line.filePrice != null ? line.filePrice * line.qty : 0);
    });
    return Array.from(groups.values()).sort((a, b) => b.total - a.total);
  }, [selectedLines]);

  // groups that end up at the same supplier share one purchase order
  const purchaseOrderCount = useMemo(
    () => new Set(selectedBySupplier.map((group) => supplierOverrides.get(group.key)?.id ?? group.supplierId ?? 'unknown')).size,
    [selectedBySupplier, supplierOverrides]
  );

  const changeGroupSupplier = (groupKey, supplierId, supplierName) => {
    setSupplierOverrides((previous) => {
      const next = new Map(previous);
      if (supplierId == null) next.delete(groupKey);
      else next.set(groupKey, { id: supplierId, name: supplierName });
      return next;
    });
  };

  // part documents (product page) of the selected parts, offered for copying onto the purchase orders; the
  // analysis only knows the files that existed when the BOM was uploaded, so prefer the list read on opening the modal
  const selectedDocumentGroups = useMemo(() => {
    const groups = new Map();
    selectedLines.forEach((line) => {
      const documents = partDocuments.byProduct ? partDocuments.byProduct.get(line.productId) : line.documents;
      if (!documents?.length || groups.has(line.productId)) return;
      groups.set(line.productId, { productId: line.productId, name: line.productName, documents });
    });
    return Array.from(groups.values());
  }, [selectedLines, partDocuments.byProduct]);
  const offeredDocumentIds = useMemo(() => selectedDocumentGroups.flatMap((group) => group.documents.map((doc) => doc.documentId)), [selectedDocumentGroups]);
  const chosenDocumentIds = offeredDocumentIds.filter((documentId) => !excludedDocumentIds.has(documentId));

  const toggleDocument = (documentId) => {
    setExcludedDocumentIds((previous) => {
      const next = new Set(previous);
      if (next.has(documentId)) next.delete(documentId);
      else next.add(documentId);
      return next;
    });
  };

  const toggleAllDocuments = () => {
    setExcludedDocumentIds((previous) => {
      const next = new Set(previous);
      if (chosenDocumentIds.length > 0) offeredDocumentIds.forEach((documentId) => next.add(documentId));
      else offeredDocumentIds.forEach((documentId) => next.delete(documentId));
      return next;
    });
  };

  const openPurchaseOrderModal = async () => {
    setPoError('');
    setPoModalOpen(true);
    const request = ++partDocumentsRequest.current;
    const productIds = Array.from(new Set(selectedLines.filter((line) => line.status === 'matched').map((line) => line.productId)));
    if (!productIds.length) {
      setPartDocuments({ loading: false, byProduct: new Map() });
      return;
    }
    setPartDocuments((previous) => ({ ...previous, loading: true }));
    try {
      const response = await fetch('/api/reports/bom-cost-analysis/part-documents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productIds })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || `Loading the part documents failed (${response.status})`);
      if (request !== partDocumentsRequest.current) return;
      setPartDocuments({ loading: false, byProduct: new Map(Object.entries(data.documents || {}).map(([productId, documents]) => [Number(productId), documents])) });
    } catch (e) {
      console.log('Error loading the BOM part documents', e);
      // the server looks the documents up again when the orders are created, the list of the analysis is good enough here
      if (request === partDocumentsRequest.current) setPartDocuments({ loading: false, byProduct: null });
    }
  };

  const handleCreatePurchaseOrders = async () => {
    try {
      setPoError('');
      setPoCreating(true);
      const payload = {
        projectId: poProjectId || null,
        reference: analysis?.fileName ? `BOM ${analysis.fileName.replace(/\.xlsx$/i, '')}` : 'RWS-BOM',
        // the server copies the part documents the parts have at that moment, minus the unticked ones
        copyDocuments: offeredDocumentIds.length === 0 || chosenDocumentIds.length > 0,
        excludedDocumentIds: offeredDocumentIds.filter((documentId) => excludedDocumentIds.has(documentId)),
        lines: selectedLines.map((line) => ({
          rowNumber: line.rowNumber,
          productId: line.productId,
          qty: line.qty,
          description: line.productName || line.partNumber,
          productRef: line.productRef,
          supplierId: line.supplierId,
          orderSupplierId: supplierOverrides.get(supplierGroupKey(line))?.id ?? null,
          price: line.unitPrice ?? line.filePrice
        }))
      };
      const response = await fetch('/api/reports/bom-cost-analysis/purchase-orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || `Purchase order creation failed (${response.status})`);
      setPoResult(data);
      setPoModalOpen(false);
      setSelectedRows(new Set());
      setPoProjectId(null);
      setSupplierOverrides(new Map());
      setExcludedDocumentIds(new Set());
    } catch (e) {
      setPoError(e.message || 'Purchase order creation failed');
    } finally {
      setPoCreating(false);
    }
  };

  const materialAccessors = useMemo(
    () => [
      null, // selection checkbox
      null, // thumbnail
      (line) => line.productName || line.partNumber,
      (line) => line.productId,
      (line) => line.productRef || null,
      (line) => line.supplierName || null,
      (line) => line.qty,
      (line) => line.stockAtelierQty,
      (line) => (line.stockLocations?.length ? (line.stockLocations[0].rack ?? 9999) * 1000 + (line.stockLocations[0].shelf ?? 999) : null),
      (line) => line.unitPrice,
      (line) => line.lineTotal,
      (line) => line.priceDateMs
    ],
    []
  );

  const supplierAccessors = useMemo(
    () => [
      (group) => group.supplierName,
      (group) => group.uniqueParts,
      (group) => group.qty,
      (group) => group.cost,
      (group) => group.cost,
      (group) => group.staleCount,
      (group) => group.unmatchedCount
    ],
    []
  );

  const sortedMaterials = useMemo(() => sortRows(filteredLines, materialAccessors, materialSort.index, materialSort.direction), [filteredLines, materialAccessors, materialSort]);
  const sortedSuppliers = useMemo(() => sortRows(supplierAggregates, supplierAccessors, supplierSort.index, supplierSort.direction), [supplierAggregates, supplierAccessors, supplierSort]);

  const materialRows = sortedMaterials.map((line) => [
    <Checkbox key={`select-${line.rowNumber}`} label="" labelHidden checked={selectedRows.has(line.rowNumber)} onChange={() => toggleRow(line.rowNumber)} />,
    line.thumbnail ? (
      <button
        key={`thumb-${line.rowNumber}`}
        type="button"
        title="Click to enlarge"
        onClick={() => setThumbPreview({ src: line.thumbnail, title: line.productName || line.partNumber || `Row ${line.rowNumber}` })}
        style={{ border: 'none', padding: 0, background: 'transparent', cursor: 'zoom-in' }}
      >
        <Thumbnail source={line.thumbnail} size="small" alt={line.productName || line.partNumber || ''} />
      </button>
    ) : (
      ''
    ),
    <InlineStack key={`name-${line.rowNumber}`} gap="200" blockAlign="center" wrap={false}>
      {line.status === 'matched' ? (
        <Link url={`/products/${line.productId}`} monochrome removeUnderline>
          <Text variant="bodyMd">{line.productName || line.partNumber || `Row ${line.rowNumber}`}</Text>
        </Link>
      ) : (
        <Text variant="bodyMd">{line.productName || line.partNumber || `Row ${line.rowNumber}`}</Text>
      )}
      {line.status === 'not_in_db' && <Badge tone="critical">ID not in DB</Badge>}
      {line.status === 'no_product_id' && <Badge tone="attention">No ProductID</Badge>}
      {line.discontinued && <Badge tone="critical">Discontinued</Badge>}
      {line.priceMissing && <Badge tone="warning">No price</Badge>}
    </InlineStack>,
    line.status === 'matched' ? <Link url={`/products/${line.productId}`}>{String(line.productId)}</Link> : line.productId ?? '—',
    line.productRef || '—',
    line.supplierId != null ? <Link url={`/companies/${line.supplierId}`}>{line.supplierName}</Link> : line.supplierName || '—',
    formatNumber(line.qty),
    line.status === 'matched' ? (
      <InlineStack key={`stock-${line.rowNumber}`} gap="200" blockAlign="center" wrap={false}>
        <Box minWidth="32px">
          <Text variant="bodyMd" alignment="end">
            {formatNumber(line.stockAtelierQty)}
          </Text>
        </Box>
        {line.pickable ? (
          <Badge tone="success">OK</Badge>
        ) : (line.stockAtelierQty ?? 0) > 0 ? (
          <Badge tone="warning">{`Short ${formatNumber(line.qty - line.stockAtelierQty)}`}</Badge>
        ) : (
          <Badge tone="critical">None</Badge>
        )}
        {line.stockTotalQty > line.stockAtelierQty && (
          <Text variant="bodySm" tone="subdued">
            {`+${formatNumber(line.stockTotalQty - line.stockAtelierQty)} elsewhere`}
          </Text>
        )}
      </InlineStack>
    ) : (
      '—'
    ),
    line.stockLocations?.length ? (
      <Text key={`loc-${line.rowNumber}`} variant="bodyMd">
        {line.stockLocations.map((location) => `${location.rack ?? '?'} / ${location.shelf ?? '?'}`).join(', ')}
      </Text>
    ) : (
      '—'
    ),
    line.status === 'matched' ? formatCurrency(line.unitPrice) : '—',
    line.status === 'matched' ? (
      <Text variant="bodyMd" fontWeight="semibold" as="span">
        {formatCurrency(line.lineTotal)}
      </Text>
    ) : (
      '—'
    ),
    line.status === 'matched' ? (
      <InlineStack key={`date-${line.rowNumber}`} gap="200" blockAlign="center" wrap={false}>
        <Text variant="bodyMd">{formatDate(line.priceUpdatedAt)}</Text>
        {line.priceStale && <Badge tone="warning">{line.priceDateMs ? `> ${STALE_PRICE_MONTHS} months` : 'Never updated'}</Badge>}
      </InlineStack>
    ) : (
      '—'
    )
  ]);

  const supplierRows = sortedSuppliers.map((group) => [
    group.supplierId != null ? <Link url={`/companies/${group.supplierId}`}>{group.supplierName}</Link> : group.supplierName,
    formatNumber(group.uniqueParts),
    formatNumber(group.qty),
    formatCurrency(group.cost),
    summary.totalCost ? `${((group.cost / summary.totalCost) * 100).toFixed(1)}%` : '—',
    group.staleCount ? <Badge tone="warning">{String(group.staleCount)}</Badge> : '0',
    group.unmatchedCount ? <Badge tone="critical">{String(group.unmatchedCount)}</Badge> : '0'
  ]);

  const coverageTone = summary.coverage >= 95 ? 'success' : summary.coverage >= 80 ? 'caution' : 'critical';

  return (
    <Page
      title="RWS BOM tool"
      subtitle="Upload an Inventor BOM export and price it against the product database"
      fullWidth
      backAction={{ content: 'Reports', url: '/reports' }}
      secondaryActions={analysis ? [{ content: 'New analysis', onAction: resetAll }] : []}
    >
      <TabTitle title="RWS BOM tool" />
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical" title="Analysis error" onDismiss={() => setError('')}>
            {error}
          </Banner>
        )}

        {poResult && <BomPurchaseOrdersBanner key={poResult.createdOrders.map((order) => order.purchaseId).join('-')} result={poResult} onDismiss={() => setPoResult(null)} />}

        {!analysis && (
          <Card>
            <Box padding="400">
              {loading ? (
                <Box padding="800">
                  <InlineStack align="center" gap="200" blockAlign="center">
                    <Spinner size="small" />
                    <Text variant="bodyMd" tone="subdued">
                      Analysing BOM…
                    </Text>
                  </InlineStack>
                </Box>
              ) : (
                <DropZone allowMultiple={false} accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onDrop={handleDrop} disabled={loading}>
                  <DropZone.FileUpload actionTitle="Upload BOM" actionHint="Inventor BOM export (.xlsx) with a ProductID column" />
                </DropZone>
              )}
            </Box>
          </Card>
        )}

        {analysis && (
          <>
            <Text variant="bodySm" tone="subdued">
              {analysis.fileName} — {summary.totalLines} lines analysed
              {analysis.skippedRows?.length ? `, ${analysis.skippedRows.length} rows skipped (missing QTY)` : ''}
            </Text>

            <InlineStack gap="400" wrap>
              <SummaryCard title="Total material cost" value={formatCurrency(summary.totalCost)} subtitle={`${summary.matchedCount} of ${summary.totalLines} lines priced from DB`} />
              <SummaryCard
                title="Database coverage"
                value={`${summary.coverage.toFixed(1)}%`}
                subtitle={`${summary.notInDb + summary.noProductId} lines unmatched (${summary.noProductId} without ProductID)`}
                tone={coverageTone}
              />
              <SummaryCard title="Unique parts" value={formatNumber(summary.uniqueParts)} subtitle={`${formatNumber(summary.totalQty)} pieces in total`} />
              <SummaryCard title="Suppliers" value={formatNumber(summary.supplierCount)} subtitle="With parts matched in DB" />
              <SummaryCard
                title="Pickable in atelier"
                value={`${formatNumber(summary.pickableCount)} / ${formatNumber(summary.matchedCount)}`}
                subtitle={summary.shortageCount ? `${summary.shortageCount} matched lines short or out of stock` : 'All matched lines fully in stock'}
                tone={summary.shortageCount ? 'caution' : 'success'}
              />
              <SummaryCard
                title="Aged prices"
                value={formatNumber(summary.staleCount)}
                subtitle={`${formatCurrency(summary.staleCost)} based on prices older than ${STALE_PRICE_MONTHS} months`}
                tone={summary.staleCount ? 'caution' : 'success'}
              />
            </InlineStack>

            {summary.notInDb + summary.noProductId > 0 && (
              <Banner tone="warning" title={`${summary.notInDb + summary.noProductId} BOM lines could not be priced`}>
                {summary.noProductId > 0 && `${summary.noProductId} lines have no ProductID in the export. `}
                {summary.notInDb > 0 && `${summary.notInDb} lines have a ProductID that does not exist in the database. `}
                Their cost is not included in the totals — check the badges in the materials table.
              </Banner>
            )}

            {summary.discontinuedCount > 0 && (
              <Banner
                tone="critical"
                title={`${summary.discontinuedCount} BOM line${summary.discontinuedCount === 1 ? ' uses a discontinued part' : 's use discontinued parts'}`}
                action={statusFilter === 'discontinued' ? undefined : { content: 'Show discontinued parts', onAction: () => setStatusFilter('discontinued') }}
              >
                These parts should no longer be used: replace them in the Inventor BOM. They are left out of select all, so they are not ordered unless you tick them yourself.
              </Banner>
            )}

            <Card padding="0">
              <Tabs tabs={RESULT_TABS} selected={selectedTab} onSelect={setSelectedTab}>
                {selectedTab === 0 ? (
                  <>
                    <Box padding="400">
                      <BlockStack gap="300">
                        <InlineStack gap="300" blockAlign="end" wrap>
                          <Box minWidth="280px">
                            <TextField
                              label="Search"
                              labelHidden
                              placeholder="Search on product, ref, ID, part number or supplier"
                              value={searchQuery}
                              onChange={setSearchQuery}
                              clearButton
                              onClearButtonClick={() => setSearchQuery('')}
                              autoComplete="off"
                            />
                          </Box>
                          <Select label="Supplier" labelInline options={supplierFilterOptions} value={supplierFilter} onChange={setSupplierFilter} />
                          <Select label="Status" labelInline options={STATUS_FILTER_OPTIONS} value={statusFilter} onChange={setStatusFilter} />
                          {hasActiveFilters && (
                            <Button
                              variant="plain"
                              onClick={() => {
                                setSearchQuery('');
                                setSupplierFilter('all');
                                setStatusFilter('all');
                              }}
                            >
                              Clear filters
                            </Button>
                          )}
                        </InlineStack>
                        <InlineStack align="space-between" blockAlign="center" wrap>
                          <Text variant="bodySm" tone="subdued">
                            {hasActiveFilters ? `${filteredLines.length} of ${enrichedLines.length} lines shown` : `${enrichedLines.length} lines`}
                          </Text>
                          <InlineStack gap="300" blockAlign="center">
                            {selectedRows.size > 0 && (
                              <>
                                <Text variant="bodySm">
                                  {selectedRows.size} selected — est. {formatCurrency(selectedTotal)}
                                </Text>
                                <Button variant="plain" onClick={() => setSelectedRows(new Set())}>
                                  Clear selection
                                </Button>
                              </>
                            )}
                            <Button variant="primary" disabled={selectedRows.size === 0} onClick={openPurchaseOrderModal}>
                              Create purchase orders
                            </Button>
                          </InlineStack>
                        </InlineStack>
                      </BlockStack>
                    </Box>
                    <DataTable
                      columnContentTypes={['text', 'text', 'text', 'numeric', 'text', 'text', 'numeric', 'text', 'text', 'numeric', 'numeric', 'text']}
                      headings={[
                        <Checkbox key="select-all" label="" labelHidden checked={allFilteredSelected ? true : someFilteredSelected ? 'indeterminate' : false} onChange={toggleAllFiltered} />,
                        '',
                        'Product',
                        'ProductID',
                        'Ref',
                        'Supplier',
                        'Qty',
                        'Stock atelier',
                        'Rack / shelf',
                        'Unit price',
                        'Total cost',
                        'Price updated'
                      ]}
                      rows={materialRows}
                      sortable={[false, false, true, true, true, true, true, true, true, true, true, true]}
                      defaultSortDirection="descending"
                      initialSortColumnIndex={10}
                      onSort={(index, direction) => setMaterialSort({ index, direction })}
                      increasedTableDensity
                      stickyHeader
                    />
                  </>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                    headings={['Supplier', 'Unique parts', 'Total qty', 'Total cost', 'Share of cost', 'Aged prices', 'Unmatched lines']}
                    rows={supplierRows}
                    sortable={[true, true, true, true, true, true, true]}
                    defaultSortDirection="descending"
                    initialSortColumnIndex={3}
                    onSort={(index, direction) => setSupplierSort({ index, direction })}
                    increasedTableDensity
                  />
                )}
              </Tabs>
            </Card>
          </>
        )}
      </BlockStack>

      <Modal
        open={poModalOpen}
        onClose={() => {
          if (!poCreating) {
            setPoModalOpen(false);
            setPoError('');
          }
        }}
        title={`Create purchase orders (${selectedLines.length} lines)`}
        size="large"
        primaryAction={{
          content: purchaseOrderCount === 1 ? 'Create 1 purchase order' : `Create ${purchaseOrderCount} purchase orders`,
          onAction: handleCreatePurchaseOrders,
          loading: poCreating,
          disabled: poCreating || selectedLines.length === 0
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setPoModalOpen(false), disabled: poCreating }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {poError && (
              <Banner tone="critical" title="Purchase order error">
                {poError}
              </Banner>
            )}
            {selectedDiscontinuedLines.length > 0 && (
              <Banner tone="critical" title={`${selectedDiscontinuedLines.length} selected line${selectedDiscontinuedLines.length === 1 ? ' is a discontinued part' : 's are discontinued parts'}`}>
                <BlockStack gap="100">
                  <Text as="p">These parts should no longer be used. Deselect them unless you are sure you want to order them.</Text>
                  <Text as="p" variant="bodySm">
                    {selectedDiscontinuedLines.map((line) => `#${line.productId} ${line.productName || line.partNumber || ''}`.trim()).join(', ')}
                  </Text>
                </BlockStack>
              </Banner>
            )}
            <Text as="p">
              One purchase order is created per supplier, with the prices from the database. Pick another supplier for a group to order those lines elsewhere; lines that are not matched in the
              database go to the unknown supplier unless you pick one.
            </Text>
            <BlockStack gap="200">
              {selectedBySupplier.map((group) => {
                const override = supplierOverrides.get(group.key);
                const changed = override != null && override.id !== group.supplierId;
                return (
                  <Box key={group.key} padding="300" borderWidth="025" borderColor="border" borderRadius="200">
                    <InlineGrid columns={{ xs: 1, md: 2 }} gap="300" alignItems="center">
                      <BlockStack gap="050">
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="span" fontWeight="semibold">
                            {group.supplierName}
                          </Text>
                          {group.supplierId == null && <Badge tone="attention">Not in DB</Badge>}
                          {changed && <Badge tone="info">Supplier changed</Badge>}
                        </InlineStack>
                        <Text as="span" variant="bodySm" tone="subdued">
                          {`${group.lineCount} line${group.lineCount === 1 ? '' : 's'} · est. ${formatCurrency(group.total)}`}
                        </Text>
                      </BlockStack>
                      <Box>
                        <CompanySelect
                          label="Order from"
                          value={override?.id ?? group.supplierId}
                          placeholder="Unknown supplier"
                          isClearable={override != null}
                          isDisabled={poCreating}
                          onChange={(supplierId, _id, supplierName) => changeGroupSupplier(group.key, supplierId, supplierName)}
                        />
                      </Box>
                    </InlineGrid>
                  </Box>
                );
              })}
            </BlockStack>
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                Part documents
              </Text>
              {partDocuments.loading ? (
                <InlineStack gap="200" blockAlign="center">
                  <Spinner size="small" />
                  <Text as="span" tone="subdued">
                    Checking the documents of the selected parts…
                  </Text>
                </InlineStack>
              ) : offeredDocumentIds.length === 0 ? (
                <Text as="p" tone="subdued">
                  None of the selected parts have documents on their product page.
                </Text>
              ) : (
                <>
                  <Checkbox
                    label={`Copy part documents to the purchase orders (${chosenDocumentIds.length} of ${offeredDocumentIds.length} files from ${selectedDocumentGroups.length} part${
                      selectedDocumentGroups.length === 1 ? '' : 's'
                    })`}
                    helpText="Each file goes to the purchase order of its part, like adding the part to a purchase order by hand."
                    checked={chosenDocumentIds.length === offeredDocumentIds.length ? true : chosenDocumentIds.length > 0 ? 'indeterminate' : false}
                    onChange={toggleAllDocuments}
                    disabled={poCreating}
                  />
                  <Scrollable style={{ maxHeight: '220px' }}>
                    <Box paddingInlineStart="600">
                      <BlockStack gap="200">
                        {selectedDocumentGroups.map((group) => (
                          <BlockStack gap="050" key={group.productId}>
                            <Text as="span" variant="bodySm" fontWeight="semibold">
                              #{group.productId} {group.name}
                            </Text>
                            {group.documents.map((doc) => (
                              <Checkbox
                                key={doc.documentId}
                                label={doc.filename}
                                checked={!excludedDocumentIds.has(doc.documentId)}
                                onChange={() => toggleDocument(doc.documentId)}
                                disabled={poCreating}
                              />
                            ))}
                          </BlockStack>
                        ))}
                      </BlockStack>
                    </Box>
                  </Scrollable>
                </>
              )}
            </BlockStack>
            <ProjectSelect label="Link to project (optional)" value={poProjectId} onChange={(value) => setPoProjectId(value)} isDisabled={poCreating} />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {thumbPreview && (
        <Modal open onClose={() => setThumbPreview(null)} title={thumbPreview.title}>
          <Modal.Section>
            <InlineStack align="center">
              <img src={thumbPreview.src} alt={thumbPreview.title} style={{ width: '400px', maxWidth: '100%' }} />
            </InlineStack>
          </Modal.Section>
        </Modal>
      )}
    </Page>
  );
}
